import React, { useState, useMemo } from 'react';
import { X, User, Phone, MapPin, Banknote, CheckCircle, ChevronDown, Loader2 } from 'lucide-react';
import dvhcvn from '../data/dvhcvn.json';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const STORAGE_BASE_URL = (
  process.env.REACT_APP_STORAGE_URL || `${API_BASE_URL}/storage`
).replace(/\/$/, '');
const buildApiUrl = (path) => `${API_BASE_URL}${path}`;
const getImageUrl = (image) => {
  if (!image) return 'https://via.placeholder.com/300';
  if (image.startsWith('http')) return image;

  return `${STORAGE_BASE_URL}/${image.replace(/^\/?(storage\/)?/, '')}`;
};

const Cart = ({ cart, updateQuantity, removeFromCart, handleCheckout }) => {
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: '',
    ward: '',
    city: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const parsePrice = (priceInput) => {
    if (priceInput === null || priceInput === undefined) return 0;
    if (typeof priceInput === 'number') return priceInput;
    return parseFloat(priceInput.toString().replace(/[^0-9.]/g, ''));
  };

  const cartTotal = cart.reduce((total, item) => {
    return total + (parsePrice(item.price) * item.quantity);
  }, 0);

  const BANK_ID = "MB";
  const ACCOUNT_NO = "0912345678";
  const ACCOUNT_NAME = "YEN SAO TAM AN";
  const TEMPLATE = "compact2";
  const qrUrl = `https://img.vietqr.io/image/${BANK_ID}-${ACCOUNT_NO}-${TEMPLATE}.png?amount=${cartTotal}&addInfo=THANH TOAN DON HANG&accountName=${encodeURIComponent(ACCOUNT_NAME)}`;

  const selectedProvince = useMemo(
    () => dvhcvn.find(p => p.name === customerInfo.city) || null,
    [customerInfo.city]
  );

  const wardOptions = useMemo(
    () => selectedProvince ? selectedProvince.wards : [],
    [selectedProvince]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'city') {
      setCustomerInfo(prev => ({ ...prev, city: value, ward: '' }));
      setErrors(prev => ({ ...prev, city: '', ward: '' }));
    } else {
      setCustomerInfo(prev => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!customerInfo.name.trim()) newErrors.name = 'Vui lòng nhập họ và tên';
    if (!customerInfo.phone.trim()) newErrors.phone = 'Vui lòng nhập số điện thoại';
    else if (!/^[0-9]{9,11}$/.test(customerInfo.phone.replace(/\s/g, ''))) newErrors.phone = 'Số điện thoại không hợp lệ';
    if (!customerInfo.address.trim()) newErrors.address = 'Vui lòng nhập địa chỉ';
    if (!customerInfo.city) newErrors.city = 'Vui lòng chọn tỉnh/thành phố';
    if (!customerInfo.ward) newErrors.ward = 'Vui lòng chọn phường/xã';
    return newErrors;
  };

  const submitOrderToApi = async () => {
    const payload = {
      customer_name: customerInfo.name.trim(),
      customer_phone: customerInfo.phone.trim(),
      customer_address: customerInfo.address.trim(),
      customer_ward: customerInfo.ward,
      customer_city: customerInfo.city,
      payment_method: paymentMethod,
      total_amount: Math.round(cartTotal),
      items: cart.map(item => ({
        id: item.id,
        name: item.name,
        price: parsePrice(item.price),
        quantity: item.quantity,
        image: item.image || null,
      })),
    };

    const res = await fetch(buildApiUrl('/api/orders'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const validationMessage = data.errors
        ? Object.values(data.errors).flat().join(' ')
        : '';

      throw new Error(data.message || validationMessage || 'Không thể gửi đơn hàng. Vui lòng thử lại.');
    }

    return data;
  };

  const completeCheckout = () => {
    setShowCheckoutModal(false);
    setShowQR(false);
    handleCheckout();
  };

  const handleSubmitOrder = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitError('');

    if (paymentMethod === 'transfer') {
      setShowQR(true);
      return;
    }

    setIsSubmitting(true);

    try {
      await submitOrderToApi();
      completeCheckout();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmTransfer = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await submitOrderToApi();
      completeCheckout();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenModal = () => {
    setShowQR(false);
    setCustomerInfo({ name: '', phone: '', address: '', ward: '', city: '' });
    setErrors({});
    setSubmitError('');
    setPaymentMethod('cash');
    setShowCheckoutModal(true);
  };

  const inputClass = (field) =>
    `w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981] transition ${errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`;

  const selectClass = (field) =>
    `w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981] transition appearance-none cursor-pointer ${errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`;

  return (
    <section className="py-16 bg-white min-h-[60vh]">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-serif font-bold text-gray-800 text-center mb-12">Giỏ Hàng Của Bạn</h2>
        {cart.length === 0 ? (
          <p className="text-center text-gray-500">Giỏ hàng của bạn đang trống.</p>
        ) : (
          <div>
            <div className="space-y-4">
              {cart.map(item => (
                <div key={item.id} className="flex flex-col md:flex-row items-center justify-between p-4 border rounded-2xl shadow-sm">
                  <div className="flex items-center space-x-4 mb-4 md:mb-0">
                    <img
                      src={getImageUrl(item.image)}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div>
                      <h3 className="font-medium text-gray-800">{item.name}</h3>
                      <p className="text-sm text-[#D97706] font-bold">
                        {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(parsePrice(item.price))}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 border rounded-full hover:bg-gray-100 transition">-</button>
                      <span className="w-10 text-center font-medium">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 border rounded-full hover:bg-gray-100 transition">+</button>
                    </div>
                    <p className="font-bold w-32 text-right">
                      {new Intl.NumberFormat('vi-VN').format(parsePrice(item.price) * item.quantity)}₫
                    </p>
                    <button onClick={() => removeFromCart(item.id)} className="text-red-500 hover:text-red-700">
                      <X size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-col items-end">
              <div className="text-xl font-bold mb-4">
                Tổng cộng: <span className="text-[#D97706] ml-2">
                  {new Intl.NumberFormat('vi-VN').format(cartTotal)}₫
                </span>
              </div>
              <button
                onClick={handleOpenModal}
                className="bg-[#10B981] text-white px-8 py-3 rounded-full font-medium hover:bg-[#059669] transition shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Tiến hành Thanh toán
              </button>
            </div>

            {/* --- CHECKOUT MODAL --- */}
            {showCheckoutModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative">
                  <button
                    onClick={() => setShowCheckoutModal(false)}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 bg-gray-100 rounded-full p-1 transition z-10"
                  >
                    <X size={20} />
                  </button>

                  {!showQR ? (
                    /* --- FORM NHẬP THÔNG TIN --- */
                    <div className="p-8 max-h-[90vh] overflow-y-auto">
                      <h3 className="text-2xl font-bold text-[#064E3B] mb-1">Thông Tin Đặt Hàng</h3>
                      <p className="text-gray-400 text-sm mb-6">Vui lòng điền đầy đủ thông tin để hoàn tất đơn hàng</p>

                      <div className="space-y-4">
                        {/* Họ và tên */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <User size={14} className="inline mr-1" />Họ và tên <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={customerInfo.name}
                            onChange={handleInputChange}
                            placeholder="Nguyễn Văn A"
                            className={inputClass('name')}
                          />
                          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>

                        {/* Số điện thoại */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <Phone size={14} className="inline mr-1" />Số điện thoại <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={customerInfo.phone}
                            onChange={handleInputChange}
                            placeholder="0912 345 678"
                            className={inputClass('phone')}
                          />
                          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>

                        {/* Địa chỉ */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <MapPin size={14} className="inline mr-1" />Địa chỉ (số nhà, tên đường) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="address"
                            value={customerInfo.address}
                            onChange={handleInputChange}
                            placeholder="123 Đường Lê Lợi"
                            className={inputClass('address')}
                          />
                          {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                        </div>

                        {/* Tỉnh/Thành phố */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tỉnh/Thành phố <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              name="city"
                              value={customerInfo.city}
                              onChange={handleInputChange}
                              className={selectClass('city')}
                            >
                              <option value="">-- Chọn tỉnh/thành phố --</option>
                              {dvhcvn.map(p => (
                                <option key={p.code} value={p.name}>{p.name}</option>
                              ))}
                            </select>
                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                          </div>
                          {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                        </div>

                        {/* Phường/Xã */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phường/Xã <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              name="ward"
                              value={customerInfo.ward}
                              onChange={handleInputChange}
                              disabled={!customerInfo.city}
                              className={`${selectClass('ward')} ${!customerInfo.city ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <option value="">
                                {customerInfo.city ? '-- Chọn phường/xã --' : '-- Chọn tỉnh/thành trước --'}
                              </option>
                              {wardOptions.map(w => (
                                <option key={w.code} value={w.name}>{w.name}</option>
                              ))}
                            </select>
                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                          </div>
                          {errors.ward && <p className="text-red-500 text-xs mt-1">{errors.ward}</p>}
                        </div>

                        {/* Phương thức thanh toán */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phương thức thanh toán <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setPaymentMethod('cash')}
                              className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 font-medium text-sm transition ${
                                paymentMethod === 'cash'
                                  ? 'border-[#10B981] bg-[#ECFDF5] text-[#065F46]'
                                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
                              }`}
                            >
                              <Banknote size={18} />
                              Tiền mặt
                            </button>
                            {/* <button
                              type="button"
                              di
                              onClick={() => setPaymentMethod('transfer')}
                              className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 font-medium text-sm transition ${
                                paymentMethod === 'transfer'
                                  ? 'border-[#10B981] bg-[#ECFDF5] text-[#065F46]'
                                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
                              }`}
                            >
                              <CreditCard size={18} />
                              Chuyển khoản
                            </button> */}
                          </div>
                          {paymentMethod === 'cash' && (
                            <p className="text-xs text-gray-400 mt-2 text-center">Thanh toán khi nhận hàng (COD)</p>
                          )}
                          {paymentMethod === 'transfer' && (
                            <p className="text-xs text-gray-400 mt-2 text-center">Quét mã QR để chuyển khoản sau khi đặt hàng</p>
                          )}
                        </div>

                        {/* Tổng tiền */}
                        <div className="flex justify-between items-center bg-gray-50 rounded-xl px-4 py-3">
                          <span className="text-gray-600 font-medium">Tổng thanh toán:</span>
                          <span className="text-[#D97706] font-bold text-lg">
                            {new Intl.NumberFormat('vi-VN').format(cartTotal)}₫
                          </span>
                        </div>

                        {submitError && (
                          <p className="text-red-500 text-sm text-center bg-red-50 border border-red-200 rounded-xl px-4 py-2">
                            {submitError}
                          </p>
                        )}

                        <button
                          onClick={handleSubmitOrder}
                          disabled={isSubmitting}
                          className="w-full bg-[#10B981] text-white py-3 rounded-xl font-bold hover:bg-[#059669] transition shadow-md mt-2 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 size={18} className="animate-spin" />
                              Đang xử lý...
                            </>
                          ) : (
                            paymentMethod === 'transfer' ? 'Xem mã QR thanh toán' : 'Xác nhận đặt hàng'
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* --- MÀN HÌNH QR CODE --- */
                    <div className="p-8 text-center">
                      <h3 className="text-2xl font-bold text-[#064E3B] mb-1">Thanh Toán QR</h3>
                      <p className="text-gray-500 text-sm mb-1">Quét mã VietQR để thanh toán đơn hàng</p>
                      <p className="text-xs text-gray-400 mb-5">
                        Đơn hàng của <span className="font-semibold text-gray-600">{customerInfo.name}</span> — {customerInfo.phone}
                      </p>

                      <div className="bg-white p-2 rounded-xl border-2 border-[#10B981] inline-block mb-6 shadow-sm">
                        <img src={qrUrl} alt="VietQR Payment" className="w-64 h-64 object-contain" />
                      </div>

                      <div className="space-y-2 bg-gray-50 p-4 rounded-xl text-sm mb-6">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Tổng thanh toán:</span>
                          <span className="font-bold text-[#D97706] text-lg">{new Intl.NumberFormat('vi-VN').format(cartTotal)}₫</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Ngân hàng:</span>
                          <span className="font-medium text-gray-800">MBBank</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Số tài khoản:</span>
                          <span className="font-medium text-gray-800">{ACCOUNT_NO}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Nội dung CK:</span>
                          <span className="font-medium text-gray-800">THANH TOAN DON HANG</span>
                        </div>
                      </div>

                      {submitError && (
                        <p className="text-red-500 text-sm text-center bg-red-50 border border-red-200 rounded-xl px-4 py-2 mb-4">
                          {submitError}
                        </p>
                      )}

                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            setSubmitError('');
                            setShowQR(false);
                          }}
                          disabled={isSubmitting}
                          className="flex-1 border border-gray-300 text-gray-600 py-3 rounded-xl font-medium hover:bg-gray-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Quay lại
                        </button>
                        <button
                          onClick={handleConfirmTransfer}
                          disabled={isSubmitting}
                          className="flex-1 bg-[#10B981] text-white py-3 rounded-xl font-bold hover:bg-[#059669] transition shadow-md flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 size={18} className="animate-spin" />
                              Đang xử lý...
                            </>
                          ) : (
                            <>
                              <CheckCircle size={18} />
                              Đã chuyển khoản
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Cart;
